import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Clock } from 'lucide-react';
import DraggableActivity from './DraggableActivity';

/**
 * PhaseSection - A collapsible section for a phase of the Play-Practice-Play plan
 *
 * Features:
 * - Collapsible header with phase info
 * - Colored left border indicating phase type
 * - Duration tracking (current vs target)
 * - Contains draggable activities
 * - Add activity button
 * - Drop zone for drag-and-drop between phases
 */
const PhaseSection = ({
  phaseKey,
  label,
  targetDuration,
  activities = [],
  onActivityMove,
  onActivityUpdate,
  onActivityRemove,
  onAddActivity,
  onDrop, // For accepting drops from other phases
  isExpanded: controlledExpanded,
  onToggleExpand
}) => {
  const [internalExpanded, setInternalExpanded] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);

  // Support both controlled and uncontrolled expanded state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const toggleExpand = onToggleExpand || (() => setInternalExpanded(!internalExpanded));

  // Calculate current duration
  const currentDuration = activities.reduce((sum, act) => sum + (act.duration_minutes || 0), 0);
  const durationDiff = currentDuration - targetDuration;
  const durationStatus = durationDiff === 0 ? 'exact' : durationDiff > 0 ? 'over' : 'under';

  // Phase-specific styling
  const phaseStyles = {
    play_1: {
      borderColor: 'var(--lrbrain-accent)',
      bgColor: 'rgba(247, 82, 27, 0.05)',
      icon: '🎮'
    },
    practice: {
      borderColor: 'var(--lrbrain-navy)',
      bgColor: 'rgba(26, 64, 114, 0.05)',
      icon: '📚'
    },
    play_2: {
      borderColor: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.05)',
      icon: '🎯'
    }
  };

  const style = phaseStyles[phaseKey] || phaseStyles.practice;

  // Handle dropping activities into this phase (empty area)
  const handlePhaseDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handlePhaseDragLeave = (e) => {
    // Only set to false if we're leaving the phase entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handlePhaseDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      // If dropping on empty area, add to end of this phase
      if (onDrop) {
        onDrop(data.activityId, data.sourcePhase, phaseKey, activities.length);
      } else {
        onActivityMove(data.activityId, data.sourcePhase, phaseKey, activities.length);
      }
    } catch (err) {
      console.error('Phase drop error:', err);
    }
  };

  return (
    <div
      className={`sp-phase sp-phase--${phaseKey} ${isDragOver ? 'sp-phase--drag-over' : ''}`}
      style={{ borderLeftColor: style.borderColor }}
    >
      {/* Header */}
      <button
        className="sp-phase__header"
        onClick={toggleExpand}
        style={{ background: isExpanded ? style.bgColor : 'transparent' }}
      >
        <div className="sp-phase__header-left">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="sp-phase__icon">{style.icon}</span>
          <span className="sp-phase__label">{label}</span>
          <span className="sp-phase__activity-count">
            ({activities.length} {activities.length === 1 ? 'activity' : 'activities'})
          </span>
        </div>

        <div className="sp-phase__header-right">
          <Clock size={14} />
          <span className={`sp-phase__duration sp-phase__duration--${durationStatus}`}>
            {currentDuration} / {targetDuration} min
            {durationDiff !== 0 && (
              <span className="sp-phase__duration-diff">
                ({durationDiff > 0 ? '+' : ''}{durationDiff})
              </span>
            )}
          </span>
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div
          className="sp-phase__content"
          onDragOver={handlePhaseDragOver}
          onDragLeave={handlePhaseDragLeave}
          onDrop={handlePhaseDrop}
        >
          {/* Activities List */}
          {activities.length > 0 ? (
            <div className="sp-phase__activities">
              {activities.map((activity, index) => (
                <DraggableActivity
                  key={activity.id || `${phaseKey}-${index}-${activity.title || 'activity'}`}
                  activity={activity}
                  phase={phaseKey}
                  index={index}
                  onMove={onActivityMove}
                  onUpdate={onActivityUpdate}
                  onRemove={onActivityRemove}
                  isOnlyActivity={activities.length === 1}
                />
              ))}
            </div>
          ) : (
            <div className="sp-phase__empty">
              <p>No activities yet. Add one or drag from another phase.</p>
            </div>
          )}

          {/* Drop Zone Indicator */}
          {isDragOver && (
            <div className="sp-phase__drop-indicator">
              Drop here to add to {label}
            </div>
          )}

          {/* Add Activity Button */}
          <button
            className="sp-phase__add-btn"
            onClick={() => onAddActivity(phaseKey)}
          >
            <Plus size={14} />
            Add Activity
          </button>
        </div>
      )}
    </div>
  );
};

export default PhaseSection;
